import type { SetupContext } from 'vue';
import { computed, onMounted, ref } from 'vue';
import type { ContentTagCategory } from '../types';

export const useSelectTags = (
  emit: SetupContext['emit'],
  categories: ContentTagCategory[],
  tags?: string[]
) => {
  const selectedCategories = ref<ContentTagCategory[]>([]);
  const selectedTags = ref<string[]>([]);

  const filteredCategories = computed(() => categories.filter(category => category.tags?.length));

  const sortedSelectedCategories = computed(() =>
    [...selectedCategories.value].sort((a: ContentTagCategory, b: ContentTagCategory) =>
      a.name.trim().localeCompare(b.name.trim())
    )
  );

  const isCategorySelected = (category: ContentTagCategory) =>
    selectedCategories.value.some(({ id }) => id === category.id);

  const deselectCategory = (category: ContentTagCategory) => {
    selectedCategories.value = selectedCategories.value.filter(
      selectedCategory => selectedCategory.id !== category.id
    );
    if (!category.tags) return;
    const tagIds = category.tags?.map(tag => tag.id);
    selectedTags.value = selectedTags.value.filter(id => !tagIds.includes(id));
    emit('update-tags', selectedTags.value);
  };

  const handleToggleSelectCategory = (category: ContentTagCategory) => {
    if (isCategorySelected(category)) {
      deselectCategory(category);
      return;
    }
    selectedCategories.value = [category, ...selectedCategories.value];
  };

  const isTagSelected = (tagId: string) => selectedTags.value.includes(tagId);

  const handleToggleSelectTag = (tagId: string) => {
    selectedTags.value = isTagSelected(tagId)
      ? selectedTags.value.filter(id => id !== tagId)
      : [tagId, ...selectedTags.value];
    emit('update-tags', selectedTags.value);
  };

  const handleClear = () => {
    selectedCategories.value = [];
    selectedTags.value = [];
    emit('update-tags', []);
  };

  onMounted(() => {
    if (!tags) return;

    selectedTags.value = [...tags];
    selectedCategories.value = categories.filter(category =>
      category.tags?.some(tag => tags.includes(tag.id))
    );
  });

  return {
    selectedCategories,
    selectedTags,
    sortedSelectedCategories,
    filteredCategories,
    isCategorySelected,
    handleToggleSelectCategory,
    isTagSelected,
    handleToggleSelectTag,
    handleClear,
    deselectCategory,
  };
};
